import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarCheck2, CheckCircle2, Download, FileSignature, FileText, FolderOpen, Loader2, ShieldCheck, UploadCloud } from 'lucide-react';
import {
  fetchDocuments,
  fetchDocumentRequests,
  uploadDocument,
  downloadInvoicePdf,
  fetchConvocations,
  downloadConvocationPdf,
  fetchWithdrawals,
} from '../api/candidate';
import { formatEuro, statusBadge } from '../lib/format';
import { dayOf, fmtClock, fmtDayFull } from '../lib/dates';
import {
  awaitsStudent,
  type CandidateDocumentRequest,
  type CandidateInvoice,
  type CandidateWithdrawal,
} from '../types';
import PendingSignatures from '../components/PendingSignatures';
import { DocumentRow as Row, WithdrawalLine } from '../components/DocumentRow';
import { subjectWords } from '../components/WithdrawalForm';
import { Badge, type BadgeTone, Button, Card, EmptyState, cx } from '../components/charte';

// Mes documents (refonte du 14/10/2026) : pièces demandées, convocations, factures et contrats — mêmes
// données, mêmes envois. Les pièces à fournir sont en tête ; sur téléphone, les boutons passent sous la ligne.
// Vente à distance (T17, 24/10/2026) : délai de rétractation des contrats et des devis signés en ligne sur leur ligne ;
// dates en toutes lettres sur toute la page.
// Boutique (T14, 26/10/2026) : la rétractation d'un achat en ligne se dit sur la ligne de sa facture, jamais dans « Contrats ».
// T14, 26/10/2026 (demandes de l'utilisateur) : le bouton « Renoncer au contrat ici » est sur la page « Conditions de
// vente », et le délai ouvert ne se dit plus que là ; ici, une ligne ne dit plus que « Rétractation reçue le … ».

const ACCEPTED_UPLOAD = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Couleur de pastille de la charte pour un statut de facture ou de contrat (libellés de lib/format). */
function statusTone(status: string): BadgeTone {
  if (status === 'paid' || status === 'signed') return 'ok';
  if (status === 'unpaid' || status === 'pending_signature') return 'warn';
  if (status === 'sent') return 'todo';
  return 'mute';
}

function StatusBadge({ status }: { status: string }) {
  return <Badge tone={statusTone(status)}>{statusBadge(status).label}</Badge>;
}

/**
 * Statut d'une facture : son état de règlement calculé par l'auto-école (F39 : réglée, à régler, en
 * retard), sinon son statut enregistré. Mêmes libellés que l'écran de l'auto-école.
 */
function InvoiceBadge({ invoice }: { invoice: CandidateInvoice }) {
  const s = invoice.settlement;
  // Payée en ligne, puis remboursée sans avoir : il reste à régler (F28).
  if (s && invoice.status === 'paid' && s.outstandingCents > 0)
    return (
      <Badge tone={s.overdue ? 'warn' : 'todo'}>
        {s.overdue ? 'Payée en ligne · en retard' : 'Payée en ligne · reste à régler'}
      </Badge>
    );
  if (!s || invoice.status === 'paid') return <StatusBadge status={invoice.status} />;
  const [label, tone]: [string, BadgeTone] =
    s.state === 'paid'
      ? ['Réglée', 'ok']
      : s.state === 'credited'
        ? ['Annulée par avoir', 'mute']
        : s.state === 'partial'
          ? [s.overdue ? 'Partiellement réglée · en retard' : 'Partiellement réglée', s.overdue ? 'warn' : 'todo']
          : [s.overdue ? 'En retard' : 'À régler', s.overdue ? 'warn' : 'todo'];
  return <Badge tone={tone}>{label}</Badge>;
}

function RequestRow({ req }: { req: CandidateDocumentRequest }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => uploadDocument(req.id, file),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['candidate', 'document-requests'] });
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message?: unknown }).message) : "Échec de l'envoi.";
      setError(msg);
    },
  });

  // Envoyée (en cours de vérification) ou validée : réglée côté élève ; refusée : à renvoyer.
  const provided = req.status === 'provided';
  const validated = req.status === 'validated';
  const rejected = req.status === 'rejected';

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de re-sélectionner le même fichier
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Fichier trop volumineux (max 10 Mo).');
      return;
    }
    upload.mutate(file);
  };

  return (
    <>
      <Row
        tone={provided || validated ? 'ok' : 'warn'}
        icon={
          validated ? (
            <ShieldCheck size={18} aria-hidden />
          ) : provided ? (
            <CheckCircle2 size={18} aria-hidden />
          ) : rejected ? (
            <AlertTriangle size={18} aria-hidden />
          ) : (
            <FileText size={18} aria-hidden />
          )
        }
        title={req.label}
        sub={
          <>
            {req.note ? req.note : `Demandé le ${fmtDayFull(dayOf(req.requestedAt))}`}
            {provided && req.fileName && <span className="mt-0.5 block truncate text-success-dark">✓ Envoyé : {req.fileName} · en cours de vérification</span>}
            {validated && <span className="mt-0.5 block text-success-dark">✓ Validé par votre auto-école</span>}
            {rejected && (
              <span className="mt-0.5 block text-danger-dark">
                {req.rejectionReason ? `Refusé : ${req.rejectionReason}` : 'Refusé : envoyez une nouvelle pièce.'}
              </span>
            )}
          </>
        }
      >
        {validated ? (
          <Badge tone="ok">Validé</Badge>
        ) : (
          <>
            <input ref={inputRef} type="file" accept={ACCEPTED_UPLOAD} className="hidden" onChange={onPick} />
            <Button
              size="sm"
              variant={provided ? 'secondary' : 'soft'}
              icon={UploadCloud}
              loading={upload.isPending}
              onClick={() => inputRef.current?.click()}
              className="max-sm:w-full"
            >
              {provided ? 'Remplacer' : rejected ? 'Renvoyer le document' : 'Envoyer le document'}
            </Button>
          </>
        )}
      </Row>
      {error && (
        <li role="alert" className="-mt-1 pb-3 pl-[52px] text-xs text-danger-dark">
          {error}
        </li>
      )}
    </>
  );
}

function RequestedDocuments() {
  const { data, isLoading } = useQuery({ queryKey: ['candidate', 'document-requests'], queryFn: fetchDocumentRequests });

  const requests = data ?? [];
  if (isLoading || requests.length === 0) return null;

  const pending = requests.filter(awaitsStudent);

  return (
    <Card className={cx(pending.length > 0 && 'ring-1 ring-warn-line/50')}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-title-sm text-heading">Documents demandés</h2>
        {pending.length > 0 && <Badge tone="warn">{pending.length} à fournir</Badge>}
      </div>
      <p className="mt-1 text-sm text-secondary">
        {pending.length > 0
          ? `Votre auto-école attend ${pending.length} document${pending.length > 1 ? 's' : ''}. Envoyez une photo ou un PDF (max 10 Mo).`
          : 'Tous les documents demandés ont été transmis.'}
      </p>
      <ul className="mt-4 divide-y divide-slate-100">
        {requests.map((req) => (
          <RequestRow key={req.id} req={req} />
        ))}
      </ul>
    </Card>
  );
}

/** Récapitulatifs de convocation à l'examen envoyés par l'auto-école (les plus récents d'abord). */
function ExamConvocations() {
  const { data } = useQuery({ queryKey: ['candidate', 'convocations'], queryFn: fetchConvocations });
  const [failed, setFailed] = useState(false);
  const convocations = data ?? [];
  if (convocations.length === 0) return null;
  return (
    <Card>
      <h2 className="text-title-sm text-heading">Convocations aux examens</h2>
      <p className="mt-1 text-sm text-secondary">
        Récapitulatifs envoyés par votre auto-école. La convocation officielle vous est envoyée par e-mail par RdvPermis :
        elle fait foi.
      </p>
      {failed && (
        <p role="alert" className="mt-3 rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
          Le téléchargement a échoué. Réessayez.
        </p>
      )}
      <ul className="mt-4 divide-y divide-slate-100">
        {convocations.map((c) => (
          <Row
            key={c.id}
            icon={<CalendarCheck2 size={18} aria-hidden />}
            title={`Examen du ${fmtDayFull(dayOf(c.examStartsAt))} à ${fmtClock(c.examStartsAt)}`}
            sub={<span className="block truncate">{c.center} · reçu le {fmtDayFull(dayOf(c.sentAt))}</span>}
          >
            <Button
              size="sm"
              icon={Download}
              onClick={() => {
                setFailed(false);
                downloadConvocationPdf(c).catch(() => setFailed(true));
              }}
              aria-label={`Télécharger la convocation de l’examen du ${fmtDayFull(dayOf(c.examStartsAt))}`}
            >
              PDF
            </Button>
          </Row>
        ))}
      </ul>
    </Card>
  );
}

export default function DocumentsView() {
  const { data, isLoading } = useQuery({ queryKey: ['candidate', 'documents'], queryFn: fetchDocuments });
  // Ventes conclues à distance (T17) : une erreur de lecture laisse la page telle qu'avant.
  const { data: sales } = useQuery({ queryKey: ['candidate', 'withdrawals'], queryFn: fetchWithdrawals });

  const invoices = data?.invoices ?? [];
  const contracts = data?.contracts ?? [];
  const withdrawals = sales ?? [];
  const listed = new Set(contracts.map((c) => c.id));
  const contractSale = (id: string) => withdrawals.find((w) => w.subject.kind === 'contract' && w.subject.id === id);
  const listedInvoices = new Set(invoices.map((i) => i.id));
  // Achat de la boutique (T14) : sa vente conclue à distance va avec sa facture.
  const invoiceSale = (id: string) => withdrawals.find((w) => w.subject.kind === 'shop_purchase' && w.subject.id === id);
  // Délai ouvert : seulement sur « Conditions de vente » ; ici, la rétractation reçue.
  const shown = (w: CandidateWithdrawal) => w.state === 'withdrawn';
  // Rétractations reçues sans ligne de contrat : les devis (absents des documents), un contrat que la lecture des
  // documents ne rend pas ou qui a échoué. Elles gardent la carte « Contrats » visible ; jamais un achat en ligne (il
  // va avec les factures).
  const extra = withdrawals.filter(
    (w) =>
      shown(w) && w.subject.kind !== 'shop_purchase' && !(w.subject.kind === 'contract' && listed.has(w.subject.id)),
  );
  // Achat dont la facture n'est pas listée (lecture des documents en échec) : sa ligne dans « Factures ».
  const shopExtra = withdrawals.filter(
    (w) => shown(w) && w.subject.kind === 'shop_purchase' && !listedInvoices.has(w.subject.id),
  );
  const empty =
    !isLoading && invoices.length === 0 && contracts.length === 0 && extra.length === 0 && shopExtra.length === 0;

  // Ligne de la rétractation : reçue seulement (le délai ouvert est sur « Conditions de vente »).
  const withdrawalLine = (sale: CandidateWithdrawal | undefined) =>
    sale && shown(sale) ? <WithdrawalLine sale={sale} /> : null;

  return (
    <div className="grid gap-5 sm:gap-6">
      <div>
        <h1 className="text-title-md text-heading sm:text-title-lg">Mes documents</h1>
        <p className="mt-1 text-sm text-secondary">Vos factures, contrats de formation et convocations aux examens.</p>
      </div>

      <PendingSignatures />

      <RequestedDocuments />

      <ExamConvocations />

      {isLoading ? (
        <Card className="flex items-center text-sm text-secondary">
          <Loader2 className="mr-2 animate-spin" size={16} /> Chargement…
        </Card>
      ) : empty ? (
        <Card>
          <EmptyState icon={FolderOpen} title="Aucun document" description="Aucun document disponible pour le moment." />
        </Card>
      ) : (
        <div className="grid gap-5 sm:gap-6 xl:grid-cols-2 xl:items-start">
          {/* Factures */}
          <Card>
            <h2 className="text-title-sm text-heading">Factures</h2>
            {invoices.length + shopExtra.length === 0 ? (
              <p className="mt-3 text-sm text-secondary">Aucune facture.</p>
            ) : (
              <ul className="mt-4 divide-y divide-slate-100">
                {invoices.map((inv) => (
                  <Row
                    key={inv.id}
                    icon={<FileText size={18} aria-hidden />}
                    title={inv.title}
                    titleId={`facture-${inv.id}-titre`}
                    sub={
                      <>
                        <span id={`facture-${inv.id}-numero`}>{inv.number}</span> · {fmtDayFull(inv.issueDate)} ·{' '}
                        <span className="font-semibold text-heading">{formatEuro(inv.totalTtc)}</span>
                        {withdrawalLine(invoiceSale(inv.id))}
                      </>
                    }
                  >
                    <InvoiceBadge invoice={inv} />
                    <Button size="sm" icon={Download} title="Télécharger la facture" onClick={() => downloadInvoicePdf(inv.id, inv.number)}>
                      PDF
                    </Button>
                  </Row>
                ))}
                {shopExtra.map((sale) => (
                  <Row
                    key={sale.id}
                    icon={<FileText size={18} aria-hidden />}
                    title={`${subjectWords(sale.subject.kind).title} ${sale.subject.number}`}
                    titleId={`achat-${sale.id}-titre`}
                    sub={
                      <>
                        {`Conclu à distance le ${fmtDayFull(sale.concludedOn)}`}
                        {withdrawalLine(sale)}
                      </>
                    }
                  />
                ))}
              </ul>
            )}
          </Card>

          {/* Contrats */}
          <Card>
            <h2 className="text-title-sm text-heading">Contrats</h2>
            {contracts.length + extra.length === 0 ? (
              <p className="mt-3 text-sm text-secondary">Aucun contrat.</p>
            ) : (
              <ul className="mt-4 divide-y divide-slate-100">
                {contracts.map((c) => (
                  <Row
                    key={c.id}
                    icon={<FileSignature size={18} aria-hidden />}
                    title={`Contrat ${c.number}`}
                    titleId={`contrat-${c.id}-titre`}
                    sub={
                      <>
                        {c.signedAt ? `Signé le ${fmtDayFull(dayOf(c.signedAt))}` : 'Non signé'}
                        {withdrawalLine(contractSale(c.id))}
                      </>
                    }
                  >
                    <StatusBadge status={c.status} />
                  </Row>
                ))}
                {extra.map((sale) => (
                  <Row
                    key={sale.id}
                    icon={<FileSignature size={18} aria-hidden />}
                    title={`${subjectWords(sale.subject.kind).title} ${sale.subject.number}`}
                    titleId={`vente-${sale.id}-titre`}
                    sub={
                      <>
                        {sale.subject.kind === 'quote'
                          ? `Accepté le ${fmtDayFull(sale.concludedOn)}`
                          : `Conclu à distance le ${fmtDayFull(sale.concludedOn)}`}
                        {withdrawalLine(sale)}
                      </>
                    }
                  />
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
